/**
 * Snowflakes Animation & Challenge Game
 * Creates falling snowflakes effect based on theme settings
 */

class SnowflakesEffect {
  constructor() {
    this.container = null;
    this.snowflakes = [];

    // Get config from the snowflake-config div
    const configEl = document.getElementById('snowflake-config');
    const dataset = configEl ? configEl.dataset : {};
    const hasSnowflakeSettings = dataset.snowflakesDensity !== undefined;

    this.config = {
      density: parseInt(dataset.snowflakesDensity) || 50,
      size: parseInt(dataset.snowflakesSize) || 10,
      speed: parseInt(dataset.snowflakesSpeed) || 5,
      color: dataset.snowflakesColor || '#ffffff',
      gameEnabled: dataset.snowflakesGameEnabled === 'true',
      showNormalSnowflakes: hasSnowflakeSettings
    };

    // Adjust density for mobile
    if (window.innerWidth < 768) {
      this.config.density = Math.floor(this.config.density * 0.5);
    }

    this.init();

    // Initialize game if enabled
    if (this.config.gameEnabled) {
      this.game = new SnowflakeGame(this);
    }
  }

  init() {
    // Create container
    this.container = document.createElement('div');
    this.container.className = 'snowflakes-container';
    document.body.appendChild(this.container);

    // Only create normal snowflakes if settings are provided
    if (this.config.showNormalSnowflakes) {
      for (let i = 0; i < this.config.density; i++) {
        this.createSnowflake();
      }

      // Handle window resize
      window.addEventListener('resize', this.handleResize.bind(this));
    }
  }

  createSnowflake(isGameMode = false, speedMultiplier = 1) {
    const snowflake = document.createElement('div');
    snowflake.className = 'snowflake';
    if (isGameMode) snowflake.classList.add('snowflake--game');
    snowflake.innerHTML = '❄';

    // Random properties
    const startX = Math.random() * window.innerWidth;
    const startDelay = Math.random() * 10;

    // Default values for game mode (independent of settings)
    const defaultGameSize = 10;
    const defaultSpeed = 5;

    const baseDuration = isGameMode
      ? (15 - defaultSpeed) + Math.random() * 5
      : (15 - this.config.speed) + Math.random() * 5;
    const duration = baseDuration / speedMultiplier; // Faster with higher multiplier
    const drift = (Math.random() - 0.5) * 100;
    const size = isGameMode
      ? (defaultGameSize * 3) + Math.random() * (defaultGameSize * 0.5)
      : this.config.size + Math.random() * (this.config.size * 0.5);
    const opacity = 0.5 + Math.random() * 0.5;

    // Set styles
    snowflake.style.left = startX + 'px';
    snowflake.style.fontSize = size + 'px';
    snowflake.style.color = this.config.color;
    snowflake.style.opacity = opacity;
    snowflake.style.animationDuration = duration + 's';
    snowflake.style.animationDelay = startDelay + 's';
    snowflake.style.setProperty('--drift', drift + 'px');

    this.container.appendChild(snowflake);
    this.snowflakes.push(snowflake);

    return snowflake;
  }

  handleResize() {
    // Adjust density on resize
    const newDensity = window.innerWidth < 768
      ? Math.floor(this.config.density * 0.5)
      : this.config.density;

    const currentCount = this.snowflakes.length;
    const diff = newDensity - currentCount;

    if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        this.createSnowflake();
      }
    } else if (diff < 0) {
      for (let i = 0; i < Math.abs(diff); i++) {
        const snowflake = this.snowflakes.pop();
        if (snowflake) {
          snowflake.remove();
        }
      }
    }
  }

  clearSnowflakes() {
    this.snowflakes.forEach(flake => flake.remove());
    this.snowflakes = [];
  }

  destroy() {
    if (this.container) {
      this.container.remove();
    }
    this.snowflakes = [];
  }
}

class SnowflakeGame {
  constructor(snowflakesEffect) {
    this.effect = snowflakesEffect;
    this.isPlaying = false;
    this.score = 0;
    this.timeLeft = 60;
    this.gameDuration = 60;
    this.timer = null;
    this.highscore = this.getHighscore();
    this.lastSnowflakeCreated = 0;
    this.minSnowflakeInterval = 50; // Minimum 50ms between snowflake creations
    this.showInstructions = !localStorage.getItem('snowflake-game-instructions-seen');

    // Item types and their properties
    this.itemTypes = {
      snowflake: { emoji: '❄', points: 1, weight: 70 },
      santa: { emoji: '🎅', points: -5, weight: 20 },
      star: { emoji: '⭐', points: 2, weight: 10 }
    };

    this.createGameButton();
    this.createGameOverlay();
    this.createInstructionsPopup();
  }

  isMobileDevice() {
    return window.innerWidth < 768;
  }

  createGameButton() {
    this.button = document.createElement('button');
    this.button.className = 'snowflake-game-btn';
    this.updateButtonText();
    this.button.addEventListener('click', () => this.startGame());
    document.body.appendChild(this.button);

    // Check if mobile on resize
    window.addEventListener('resize', () => this.updateButtonText());
  }

  updateButtonText() {
    // Check if desktop
    if (!this.isMobileDevice()) {
      this.button.innerHTML = `
        <div class="snowflake-game-btn__content">
          <span class="snowflake-game-btn__title">❄️ Schneeflocken Challenge</span>
          <span class="snowflake-game-btn__action">📱 Bitte nutzen Sie Ihr Mobilgerät</span>
        </div>
      `;
      this.button.style.cursor = 'not-allowed';
      this.button.style.opacity = '0.7';
      return;
    }

    // Reset button style for mobile
    this.button.style.cursor = 'pointer';
    this.button.style.opacity = '1';

    if (this.highscore > 0 && !this.isPlaying) {
      this.button.innerHTML = `
        <div class="snowflake-game-btn__content">
          <span class="snowflake-game-btn__title">❄️ Schneeflocken Challenge</span>
          <span class="snowflake-game-btn__highscore">Highscore: ${this.highscore}</span>
        </div>
      `;
    } else {
      this.button.innerHTML = `
        <div class="snowflake-game-btn__content">
          <span class="snowflake-game-btn__title">❄️ Schneeflocken Challenge</span>
          <span class="snowflake-game-btn__action">Jetzt starten</span>
        </div>
      `;
    }
  }

  createGameOverlay() {
    // Game UI container
    this.gameUI = document.createElement('div');
    this.gameUI.className = 'snowflake-game-ui';
    this.gameUI.style.display = 'none';

    this.gameUI.innerHTML = `
      <div class="snowflake-game-ui__hud">
        <div class="snowflake-game-ui__score">Score: <span>0</span></div>
        <div class="snowflake-game-ui__timer">Zeit: <span>60</span>s</div>
      </div>
    `;

    document.body.appendChild(this.gameUI);

    // Game over popup
    this.popup = document.createElement('div');
    this.popup.className = 'snowflake-game-popup';
    this.popup.style.display = 'none';

    this.popup.innerHTML = `
      <div class="snowflake-game-popup__content">
        <h2>🎉 Challenge beendet!</h2>
        <div class="snowflake-game-popup__stats">
          <div class="snowflake-game-popup__score">
            <div class="snowflake-game-popup__label">Dein Score</div>
            <div class="snowflake-game-popup__value" data-score>0</div>
          </div>
          <div class="snowflake-game-popup__highscore">
            <div class="snowflake-game-popup__label">🏆 Highscore</div>
            <div class="snowflake-game-popup__value" data-highscore>0</div>
          </div>
        </div>
        <div class="snowflake-game-popup__actions">
          <button class="btn btn--primary" data-play-again>Nochmal spielen</button>
          <button class="btn btn--secondary" data-close>Schließen</button>
        </div>
      </div>
    `;

    document.body.appendChild(this.popup);

    // Event listeners
    this.popup.querySelector('[data-play-again]').addEventListener('click', () => {
      this.hidePopup();
      this.startGame();
    });

    this.popup.querySelector('[data-close]').addEventListener('click', () => {
      this.hidePopup();
    });
  }

  createInstructionsPopup() {
    this.instructionsPopup = document.createElement('div');
    this.instructionsPopup.className = 'snowflake-game-popup';
    this.instructionsPopup.style.display = 'none';

    this.instructionsPopup.innerHTML = `
      <div class="snowflake-game-popup__content">
        <h2>❄️ Schneeflocken Challenge</h2>
        <div class="snowflake-instructions">
          <div class="snowflake-instructions__item">
            <span class="snowflake-instructions__emoji">❄</span>
            <span class="snowflake-instructions__text">Schneeflocke = +1 Punkt</span>
          </div>
          <div class="snowflake-instructions__item">
            <span class="snowflake-instructions__emoji">⭐</span>
            <span class="snowflake-instructions__text">Stern = +2 Punkte</span>
          </div>
          <div class="snowflake-instructions__item snowflake-instructions__item--danger">
            <span class="snowflake-instructions__emoji">🎅</span>
            <span class="snowflake-instructions__text">Weihnachtsmann = -5 Punkte</span>
          </div>
        </div>
        <p class="snowflake-instructions__santa-hint">
          Psst... der Weihnachtsmann ist noch nicht dran! Der schläft noch bis zum 24.12. – nicht wecken, sonst platzt er vor Schreck!
        </p>
        <p class="snowflake-instructions__timer">Du hast 1 Minute Zeit!</p>
        <div class="snowflake-game-popup__actions">
          <button class="btn btn--primary" data-start-game>Jetzt spielen!</button>
        </div>
      </div>
    `;

    document.body.appendChild(this.instructionsPopup);

    this.instructionsPopup.querySelector('[data-start-game]').addEventListener('click', () => {
      localStorage.setItem('snowflake-game-instructions-seen', 'true');
      this.showInstructions = false;
      this.hideInstructionsPopup();
      this.actuallyStartGame();
    });
  }

  showDesktopWarning() {
    // Create temporary popup for desktop warning
    const warningPopup = document.createElement('div');
    warningPopup.className = 'snowflake-game-popup';
    warningPopup.innerHTML = `
      <div class="snowflake-game-popup__content">
        <h2>📱 Mobilgerät erforderlich</h2>
        <p style="font-size: 16px; color: #6b7280; margin: 24px 0; line-height: 1.6;">
          Die Schneeflocken Challenge ist für mobile Geräte optimiert.
          Bitte öffnen Sie diese Seite auf Ihrem Smartphone oder Tablet, um zu spielen.
        </p>
        <div class="snowflake-game-popup__actions">
          <button class="btn btn--primary" data-close-warning>OK, verstanden</button>
        </div>
      </div>
    `;

    document.body.appendChild(warningPopup);

    // Close button handler
    warningPopup.querySelector('[data-close-warning]').addEventListener('click', () => {
      warningPopup.remove();
    });

    // Close on background click
    warningPopup.addEventListener('click', (e) => {
      if (e.target === warningPopup) {
        warningPopup.remove();
      }
    });
  }

  startGame() {
    if (this.isPlaying) return;

    // Prevent game on desktop
    if (!this.isMobileDevice()) {
      this.showDesktopWarning();
      return;
    }

    // Show instructions popup if first time
    if (this.showInstructions) {
      this.showInstructionsPopup();
      return;
    }

    this.actuallyStartGame();
  }

  actuallyStartGame() {
    if (this.isPlaying) return;

    this.isPlaying = true;
    this.score = 0;
    this.timeLeft = this.gameDuration;
    this.initialDensity = this.effect.config.density;
    this.difficultyInterval = null;

    // Hide button
    this.button.style.display = 'none';

    // Show game UI
    this.gameUI.style.display = 'block';
    this.updateGameUI();

    // Clear normal snowflakes
    this.effect.clearSnowflakes();

    // Create game snowflakes with click handlers
    this.createGameSnowflakes();

    // Start timer
    this.startTimer();

    // Start difficulty progression
    this.startDifficultyProgression();

    // Add background overlay
    document.body.classList.add('snowflake-game-active');
  }

  createGameSnowflakes() {
    for (let i = 0; i < this.effect.config.density; i++) {
      this.createGameItem();
    }
  }

  getDifficultyMultiplier() {
    // Progress from 0 (start) to 1 (end)
    const progress = 1 - (this.timeLeft / this.gameDuration);
    return progress;
  }

  getRandomItemType() {
    const progress = this.getDifficultyMultiplier();

    // Increase santa weight as game progresses (from 20 to 60)
    const santaWeight = 20 + (progress * 40);

    // Adjust weights based on difficulty
    const adjustedTypes = {
      snowflake: { ...this.itemTypes.snowflake, weight: 70 - (progress * 30) },
      santa: { ...this.itemTypes.santa, weight: santaWeight },
      star: { ...this.itemTypes.star, weight: 10 }
    };

    const totalWeight = Object.values(adjustedTypes).reduce((sum, type) => sum + type.weight, 0);
    let random = Math.random() * totalWeight;

    for (const [name, type] of Object.entries(adjustedTypes)) {
      random -= type.weight;
      if (random <= 0) {
        return { name, ...this.itemTypes[name] };
      }
    }

    return { name: 'snowflake', ...this.itemTypes.snowflake };
  }

  createGameItem() {
    const itemType = this.getRandomItemType();
    const progress = this.getDifficultyMultiplier();

    // Speed increases from 1x to 5x for snowflakes and santas
    let speedMultiplier = 1 + (progress * 4);

    // Stars are 2x faster at the end (so 10x total speed at end)
    if (itemType.name === 'star') {
      speedMultiplier *= 2;
    }

    const snowflake = this.effect.createSnowflake(true, speedMultiplier);
    snowflake.innerHTML = itemType.emoji;
    snowflake.dataset.points = itemType.points;
    snowflake.dataset.itemType = itemType.name;
    snowflake.style.cursor = 'pointer';

    // Add both click and touch events for better mobile support
    const clickHandler = (e) => this.handleSnowflakeClick(e, snowflake);
    snowflake.addEventListener('click', clickHandler);
    snowflake.addEventListener('touchstart', clickHandler, { passive: false });

    return snowflake;
  }

  handleSnowflakeClick(e, snowflake) {
    e.preventDefault();
    e.stopPropagation();

    if (!this.isPlaying) return;

    // Prevent multiple clicks on same snowflake
    if (snowflake.classList.contains('snowflake--clicked')) return;

    // Get points from data attribute
    const points = parseInt(snowflake.dataset.points) || 1;
    const itemType = snowflake.dataset.itemType;

    this.score += points;
    this.updateGameUI();

    // Check if it's a santa - trigger explosion
    if (itemType === 'santa') {
      this.explodeSanta(snowflake);
    }

    // Animate snowflake out
    snowflake.classList.add('snowflake--clicked');

    // Remove click listener immediately
    snowflake.style.pointerEvents = 'none';

    setTimeout(() => {
      snowflake.remove();
      const index = this.effect.snowflakes.indexOf(snowflake);
      if (index > -1) {
        this.effect.snowflakes.splice(index, 1);
      }

      // Throttle snowflake creation to prevent performance issues
      if (this.isPlaying) {
        const now = Date.now();
        const timeSinceLastCreation = now - this.lastSnowflakeCreated;

        if (timeSinceLastCreation >= this.minSnowflakeInterval) {
          // Immediate creation if enough time has passed
          this.lastSnowflakeCreated = now;
          requestAnimationFrame(() => {
            this.createGameItem();
          });
        } else {
          // Delay creation to maintain minimum interval
          const delay = this.minSnowflakeInterval - timeSinceLastCreation;
          setTimeout(() => {
            if (this.isPlaying) {
              this.lastSnowflakeCreated = Date.now();
              requestAnimationFrame(() => {
                this.createGameItem();
              });
            }
          }, delay);
        }
      }
    }, 300);
  }

  explodeSanta(santaElement) {
    // Get santa position
    const santaRect = santaElement.getBoundingClientRect();
    const santaX = santaRect.left + santaRect.width / 2;
    const santaY = santaRect.top + santaRect.height / 2;

    // Explosion radius in pixels
    const explosionRadius = 150;

    // Create visual explosion effect
    const explosion = document.createElement('div');
    explosion.className = 'santa-explosion';
    explosion.style.left = (santaX - explosionRadius) + 'px';
    explosion.style.top = (santaY - explosionRadius) + 'px';
    explosion.style.width = (explosionRadius * 2) + 'px';
    explosion.style.height = (explosionRadius * 2) + 'px';
    document.body.appendChild(explosion);

    // Remove explosion effect after animation
    setTimeout(() => {
      explosion.remove();
    }, 600);

    // Find all elements within explosion radius
    const elementsToDestroy = [];

    this.effect.snowflakes.forEach(element => {
      // Skip the santa itself and already clicked elements
      if (element === santaElement || element.classList.contains('snowflake--clicked')) {
        return;
      }

      // Get element position
      const rect = element.getBoundingClientRect();
      const elementX = rect.left + rect.width / 2;
      const elementY = rect.top + rect.height / 2;

      // Calculate distance
      const distance = Math.sqrt(
        Math.pow(elementX - santaX, 2) + Math.pow(elementY - santaY, 2)
      );

      // If within explosion radius, mark for destruction
      if (distance <= explosionRadius) {
        elementsToDestroy.push(element);
      }
    });

    // Destroy all elements in explosion radius
    elementsToDestroy.forEach(element => {
      // Mark as clicked to prevent double-clicking
      element.classList.add('snowflake--clicked');
      element.style.pointerEvents = 'none';

      // Animate out
      setTimeout(() => {
        element.remove();
        const index = this.effect.snowflakes.indexOf(element);
        if (index > -1) {
          this.effect.snowflakes.splice(index, 1);
        }
      }, 300);
    });
  }

  startTimer() {
    this.timer = setInterval(() => {
      this.timeLeft--;
      this.updateGameUI();

      if (this.timeLeft <= 0) {
        this.endGame();
      }
    }, 1000);
  }

  startDifficultyProgression() {
    // Add more items every 2 seconds
    this.difficultyInterval = setInterval(() => {
      if (!this.isPlaying) {
        clearInterval(this.difficultyInterval);
        return;
      }

      const progress = this.getDifficultyMultiplier();
      const currentDensity = this.effect.snowflakes.length;

      // Increase density from 1x to 5x
      const targetDensity = Math.floor(this.initialDensity * (1 + progress * 4));

      // Add items if below target - more items at once towards the end
      if (currentDensity < targetDensity) {
        const maxItemsToAdd = Math.floor(5 + progress * 5); // 5-10 items depending on progress
        const itemsToAdd = Math.min(maxItemsToAdd, targetDensity - currentDensity);
        for (let i = 0; i < itemsToAdd; i++) {
          this.createGameItem();
        }
      }
    }, 2000);
  }

  updateGameUI() {
    this.gameUI.querySelector('.snowflake-game-ui__score span').textContent = this.score;
    this.gameUI.querySelector('.snowflake-game-ui__timer span').textContent = this.timeLeft;
  }

  endGame() {
    this.isPlaying = false;
    clearInterval(this.timer);
    if (this.difficultyInterval) {
      clearInterval(this.difficultyInterval);
    }

    // Update highscore
    if (this.score > this.highscore) {
      this.highscore = this.score;
      this.saveHighscore(this.score);
    }

    // Hide game UI
    this.gameUI.style.display = 'none';

    // Remove game snowflakes
    this.effect.clearSnowflakes();

    // Remove background overlay
    document.body.classList.remove('snowflake-game-active');

    // Show popup
    this.showPopup();

    // Show button again
    this.button.style.display = 'flex';
    this.updateButtonText();

    // Recreate normal snowflakes only if they were enabled
    if (this.effect.config.showNormalSnowflakes) {
      for (let i = 0; i < this.effect.config.density; i++) {
        this.effect.createSnowflake(false);
      }
    }
  }

  showPopup() {
    this.popup.querySelector('[data-score]').textContent = this.score;
    this.popup.querySelector('[data-highscore]').textContent = this.highscore;
    this.popup.style.display = 'flex';
  }

  hidePopup() {
    this.popup.style.display = 'none';
  }

  showInstructionsPopup() {
    this.instructionsPopup.style.display = 'flex';
  }

  hideInstructionsPopup() {
    this.instructionsPopup.style.display = 'none';
  }

  getHighscore() {
    try {
      return parseInt(localStorage.getItem('snowflake-game-highscore')) || 0;
    } catch (e) {
      return 0;
    }
  }

  saveHighscore(score) {
    try {
      localStorage.setItem('snowflake-game-highscore', score.toString());
    } catch (e) {
      console.error('Could not save highscore:', e);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const configEl = document.getElementById('snowflake-config');
    if (configEl && configEl.dataset.snowflakesEnabled === 'true') {
      new SnowflakesEffect();
    }
  });
} else {
  const configEl = document.getElementById('snowflake-config');
  if (configEl && configEl.dataset.snowflakesEnabled === 'true') {
    new SnowflakesEffect();
  }
}
